import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import { Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useFetchSettlementLoadsQuery, useFetchSettlementSummaryQuery } from '@api/Trinium/Endpoints/Settlements/slice';
import { DeductionBreakdownLine } from '@api/Trinium/Endpoints/Settlements/types';
import ReportSkeleton from '../ReportSkeleton';
import { IStatementTab } from '../types';
import LoadsByServicePopper, { LoadCommissionLine, LoadsByServiceTarget } from './LoadsByServicePopper';
import { groupLoadsByService } from './types';

import styles from './SummaryTab.module.css';

const cx = classNames.bind(styles);

/**
 * Counting readouts need a far longer budget than UI transitions do — the eye has to track digits
 * churning, not a single position settling. Sub-second durations read as an instant jump.
 */
const COUNT_UP_DURATION_MS = 1800;

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

/** Accounting-style currency: negative values render red in parentheses. */
function Amount({ value }: { value: number }) {
  return (
    <span className={cx({ amount: true, negative: value < 0 })}>
      {value < 0 ? `(${currencyFormatter.format(-value)})` : currencyFormatter.format(value)}
    </span>
  );
}

/**
 * Quadratic rather than the cubic/expo curves used for movement: those reach ~90% of the target in
 * the first third of their duration, which on a number reads as a jump followed by a stall.
 */
function easeOutQuad(progress: number) {
  return 1 - (1 - progress) ** 2;
}

/**
 * Ramps 0 → target across COUNT_UP_DURATION_MS. Deliberately not gated on prefers-reduced-motion:
 * Windows reports `reduce` whenever its master visual-effects toggle is off, which is common on
 * performance-trimmed dispatcher machines and would suppress the count-up for everyone.
 */
function useCountUp(target: number, enabled: boolean) {
  const [value, setValue] = useState(enabled ? 0 : target);

  useEffect(() => {
    if (!enabled) {
      setValue(target);
      return;
    }

    let frame = 0;
    let startedAt: number | undefined;

    const step = (timestamp: number) => {
      startedAt ??= timestamp;
      const progress = Math.min(1, (timestamp - startedAt) / COUNT_UP_DURATION_MS);
      setValue(target * easeOutQuad(progress));

      if (progress < 1) {
        frame = requestAnimationFrame(step);
      }
    };

    frame = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [target, enabled]);

  return value;
}

/**
 * An Amount that counts from $0.00 to its value when it first appears. Kept separate from Amount so
 * only the Breakdown rail animates — the tables hold dozens of figures and would read as noise.
 */
function AnimatedAmount({ value, animate }: { value: number; animate: boolean }) {
  const animated = useCountUp(value, animate);
  return <Amount value={animated} />;
}

/** Keeps a card's shape when it has no rows. */
function PlaceholderRow({ text }: { text: string }) {
  return (
    <TableRow>
      <TableCell
        colSpan={2}
        className={styles['empty-row']}>
        {text}
      </TableCell>
    </TableRow>
  );
}

function SummaryCard({
  title,
  labelHeader,
  amountHeader,
  total,
  children
}: {
  title: string;
  labelHeader: string;
  amountHeader: string;
  total?: number;
  children: ReactNode;
}) {
  return (
    <Paper
      variant='outlined'
      className={styles.card}>
      <div className={styles['card-title']}>
        <h3 className={styles['card-heading']}>{title}</h3>
        {total !== undefined && (
          <span className={styles['card-total']}>
            Grand Total: <Amount value={total} />
          </span>
        )}
      </div>
      <TableContainer className={styles['card-body']}>
        <Table
          size='small'
          stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{labelHeader}</TableCell>
              <TableCell align='right'>{amountHeader}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

/** One side of the split Deduction Breakdown: sticky title, independently scrolling rows, sticky subtotal. */
function DeductionColumn({ label, lines }: { label: string; lines: DeductionBreakdownLine[] }) {
  const sortedLines = [...lines].sort((a, b) => b.amount - a.amount);
  const subtotal = lines.reduce((total, line) => total + line.amount, 0);
  return (
    <div className={styles['deduction-column']}>
      <div className={styles['deduction-column-title']}>{label}</div>
      <TableContainer className={styles['deduction-column-body']}>
        <Table size='small'>
          <TableBody>
            {sortedLines.length === 0 && <PlaceholderRow text='None' />}
            {sortedLines.map((line, index) => (
              <TableRow key={index}>
                <TableCell>{line.serviceDescription}</TableCell>
                <TableCell align='right'>
                  <Amount value={line.amount} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={styles['deduction-subtotal']}>
        <span>{label} Total</span>
        <Amount value={subtotal} />
      </div>
    </div>
  );
}

function DeductionCard({
  total,
  oneTime,
  recurring
}: {
  total: number;
  oneTime: DeductionBreakdownLine[];
  recurring: DeductionBreakdownLine[];
}) {
  return (
    <Paper
      variant='outlined'
      className={styles.card}>
      <div className={styles['card-title']}>
        <h3 className={styles['card-heading']}>Deduction Breakdown</h3>
        <span className={styles['card-total']}>
          Grand Total: <Amount value={total} />
        </span>
      </div>
      <div className={styles['deduction-columns']}>
        <DeductionColumn
          label='One-Time Deductions'
          lines={oneTime}
        />
        <DeductionColumn
          label='Recurring Deductions'
          lines={recurring}
        />
      </div>
    </Paper>
  );
}

/** A Load Commissions Breakdown line, clickable to reveal the loads behind it. */
function LoadCommissionRow({
  line,
  isOpen,
  onToggle
}: {
  line: LoadCommissionLine;
  isOpen: boolean;
  onToggle: (anchorEl: HTMLElement, line: LoadCommissionLine) => void;
}) {
  return (
    <TableRow
      hover
      selected={isOpen}
      tabIndex={0}
      className={styles['clickable-row']}
      onClick={(event) => {
        // Keeps the popup's ClickAwayListener (a document-level listener) from treating a click on
        // another breakdown row as a click-away, which would close the popup the row just opened.
        event.stopPropagation();
        onToggle(event.currentTarget, line);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle(event.currentTarget, line);
        }
      }}>
      <TableCell>{line.serviceDescription}</TableCell>
      <TableCell align='right'>
        <Amount value={line.amount} />
      </TableCell>
    </TableRow>
  );
}

export default function SummaryTab(props: IStatementTab) {
  const statement = { terminalNumber: props.terminalNumber, statementId: props.statementId };
  const { data: summary, isFetching: isFetchingSummary } = useFetchSettlementSummaryQuery(statement);
  const { data: loads, isFetching: isFetchingLoads } = useFetchSettlementLoadsQuery(statement);

  /**
   * Page.tsx unmounts this tab on every tab switch, so only the first reveal of a statement animates.
   * A remount that reads straight from the RTK cache shows the figures immediately instead of
   * replaying the ramp and making the primary readout unreadable again.
   */
  const [animateTotals] = useState(() => summary === undefined || loads === undefined);

  const [drilldown, setDrilldown] = useState<LoadsByServiceTarget | null>(null);

  const loadCommissionBreakdown = useMemo(() => groupLoadsByService(loads ?? []), [loads]);

  /** Clicking the row whose popup is already open closes it. */
  const handleToggleDrilldown = useCallback((anchorEl: HTMLElement, line: LoadCommissionLine) => {
    setDrilldown((previous) => (previous?.anchorEl === anchorEl ? null : { anchorEl, line }));
  }, []);

  const closeDrilldown = useCallback(() => {
    setDrilldown(null);
  }, []);

  if (isFetchingSummary || isFetchingLoads || !summary || !loads) {
    return <ReportSkeleton />;
  }

  // Load commission, adjustments and deductions sum to the statement's issued check to the cent.
  const loadCommission = loadCommissionBreakdown.reduce((total, line) => total + line.amount, 0);
  const totalCommission = loadCommission + summary.commissionAdjustments;
  const netCheck = totalCommission + summary.deductions;
  const commissionAdjustmentBreakdown = [...summary.commissionAdjustmentBreakdown].sort((a, b) => b.amount - a.amount);
  const oneTimeDeductions = summary.deductionBreakdown.filter((line) => line.deductionType === 'One-Time');
  const recurringDeductions = summary.deductionBreakdown.filter((line) => line.deductionType === 'Recurring');
  // Most recent check first so the selected statement's check is visible without scrolling.
  const ytdChecks = [...summary.ytdChecks].reverse();
  // Sum of the checks actually issued. This is not expected to equal the rail's 1099 YTD Earnings.
  const ytdCheckTotal = summary.ytdChecks.reduce((total, check) => total + check.amount, 0);

  return (
    <div className={styles.container}>
      <Paper
        variant='outlined'
        className={styles.rail}>
        <h3 className={styles['rail-title']}>Breakdown</h3>

        <div className={styles['rail-rows']}>
          <div className={styles['rail-row']}>
            <span className={styles['rail-label']}>Load Commission</span>
            <AnimatedAmount
              value={loadCommission}
              animate={animateTotals}
            />
          </div>
          <div className={styles['rail-row']}>
            <span className={styles['rail-label']}>Commission Adjustments</span>
            <AnimatedAmount
              value={summary.commissionAdjustments}
              animate={animateTotals}
            />
          </div>
          <div className={styles['rail-row']}>
            <span className={styles['rail-label']}>Total Commission</span>
            <AnimatedAmount
              value={totalCommission}
              animate={animateTotals}
            />
          </div>
          <div className={styles['rail-row']}>
            <span className={styles['rail-label']}>Deductions</span>
            <AnimatedAmount
              value={summary.deductions}
              animate={animateTotals}
            />
          </div>
        </div>

        <Divider />

        <div className={styles['net-check']}>
          <span>Net Check</span>
          <AnimatedAmount
            value={netCheck}
            animate={animateTotals}
          />
        </div>

        <div className={styles['ytd-earnings']}>
          <span>1099 YTD Earnings</span>
          <AnimatedAmount
            value={summary.earnings1099Ytd}
            animate={animateTotals}
          />
        </div>
      </Paper>

      <div className={styles['card-grid']}>
        <SummaryCard
          title='Load Commissions Breakdown'
          labelHeader='Service'
          amountHeader='Commission Total'
          total={loadCommission}>
          {loadCommissionBreakdown.length === 0 && <PlaceholderRow text='None' />}
          {loadCommissionBreakdown.map((line) => (
            <LoadCommissionRow
              key={line.serviceCode}
              line={line}
              isOpen={drilldown?.line.serviceCode === line.serviceCode}
              onToggle={handleToggleDrilldown}
            />
          ))}
        </SummaryCard>

        <SummaryCard
          title='Commission Adjustments'
          labelHeader='Service'
          amountHeader='Amount'
          total={summary.commissionAdjustments}>
          {commissionAdjustmentBreakdown.length === 0 && <PlaceholderRow text='None' />}
          {commissionAdjustmentBreakdown.map((line, index) => (
            <TableRow key={index}>
              <TableCell>{line.serviceDescription}</TableCell>
              <TableCell align='right'>
                <Amount value={line.amount} />
              </TableCell>
            </TableRow>
          ))}
        </SummaryCard>

        <DeductionCard
          total={summary.deductions}
          oneTime={oneTimeDeductions}
          recurring={recurringDeductions}
        />

        <SummaryCard
          title='YTD Commissions'
          labelHeader='Check Date'
          amountHeader='Amount'
          total={ytdCheckTotal}>
          {ytdChecks.map((check, index) => (
            <TableRow
              key={check.checkDate.unix}
              selected={index === 0}>
              <TableCell>{check.checkDate.shortDateExtended}</TableCell>
              <TableCell align='right'>
                <Amount value={check.amount} />
              </TableCell>
            </TableRow>
          ))}
        </SummaryCard>
      </div>

      <LoadsByServicePopper
        target={drilldown}
        onClose={closeDrilldown}
      />
    </div>
  );
}
